import { Component, Input, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, catchError, debounceTime, map, of, switchMap } from 'rxjs';

import { Company } from '../../../models/company.model';
import { OrderRequestCompany, OrderProduct } from '../../../models/order.model';
import { Products } from '../../../models/product.model';
import { CompanyService } from '../../../shared/services/company/company.services';
import { ProductService } from '../../../shared/services/products/product.service';
import { FormValidation } from '../../../shared/services/utils/form-validation.service';
import { showNotification } from '../../../consts/utils';
import { MatSnackBar } from '@angular/material/snack-bar';

import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { SharedModule } from '../../../shared/shared.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PackageService } from '../../../shared/services/superadminusers/plan.service';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';

interface Course {
  id: string;
  name: string;
  price?: number;
}

@Component({
  selector: 'app-add-course-company-modal',
  standalone: true,
  imports: [LoaderComponent, NgSelectModule, CommonModule, FormsModule],
  templateUrl: './add-course-company-modal.component.html',
  styleUrls: ['./add-course-company-modal.component.scss'],
})
export class AddCourseCompanyModalComponent {

  @Input() company: Company;


  order: OrderRequestCompany = { products: [], total: 0 };

  courses: Observable<Products[]>;
  coursesDefault: Observable<Products[]>;
  typeahead: Subject<string> = new Subject<string>();

  product: OrderProduct;
  selectedProduct: string;
  @Output() refreshTable: any;

  public validate = false;

  public courseError: string | null;
  public quantityError: string | null;
  public priceError: string | null;
  public isLoading: boolean = false;


  // In your component class:
  availableCourses: Course[] = []; // Use the interface for type safety
  selectedCourses: Course[] = []; // Changed to store Course objects instead of just IDs


  constructor(private modal: NgbModal,
    private _productService: ProductService,
    private _companyService: CompanyService,
    public formValidation: FormValidation,
    private snackBar: MatSnackBar,
    private _packageService: PackageService,
  ) { }

  ngOnInit(): void {
    this.loadAvailableCourses()
    this.product = {
      productName: "",
      productId: "",
      quantity: 0,
      price: 0,
      totalProduct: 0
    }

    this._productService.filter("", "most_recent", 1, 10).subscribe({
      next: (response) => {
        this.coursesDefault = response.data;
        this.createFilterTypeahead();
      },
      error: ({ error }) => {
        showNotification(this.snackBar, error.message, 'error');
      },
    });

  }

  createFilterTypeahead() {
    this.courses = this.typeahead.pipe(
      debounceTime(300),
      switchMap(term => {
        if (!term || term.length < 2) {
          return of(this.coursesDefault);
        } else {
          return this._productService.filter(term, "most_recent", 1, 10).pipe(
            map(response => { return response.data }),
            catchError(() => of(this.coursesDefault))
          )
        }
      })
    );
    console.log(" this.courses", this.courses);

  }

  createOrUpdateOrder() {

    // Validate if there are any products in the order
    if (!this.order.products || this.order.products.length === 0) {
      showNotification(this.snackBar, 'Please add at least one course before sending payment link', 'error');
      return;
    }


    this._companyService.saveOrderCompany(this.company._id, this.order).subscribe({
      next: (response: any) => {
        this.isLoading = false; // Hide loader
        showNotification(this.snackBar, response.message, 'success');

        this.close()
        this.refreshTable();
      },
      error: (error) => {
        this.isLoading = false; // Hide loader
        showNotification(this.snackBar, error.message, 'error');
      },
    })
  }

  clearOrderProduct() {
    this.selectedProduct = "";
    this.product.productId = "";
    this.product.quantity = 0;
    this.product.price = 0;
    this.product.totalProduct = 0;
  }




  addProduct() {
    if (this.validateAddProduct()) {
      return;
    }
    if (this.validateRepeatedProduct(this.product.productId)) {
      return;
    }
    let product: OrderProduct = {
      productId: this.product.productId,
      productName: this.product.productName,
      quantity: this.product.quantity,
      price: this.product.price,
      totalProduct: this.product.totalProduct
    }
    this.order.products.push(product);
    this.clearOrderProduct();
    this.calculeTotal();
  }

  // Update clearSelection()
  clearSelection() {
    this.selectedCourses = [];
    this.clearOrderProduct();
  }

  removeProduct(product: OrderProduct) {
    // Find the index of the product to remove
    const index = this.order.products.indexOf(product);

    if (index > -1) {
      this.order.products.splice(index, 1);
      this.calculeTotal(); // Add this line to recalculate the total
    }
  }
  onCoursesSelected(selectedItems: any[]) {
    // If you only care about the last selected item
    const lastSelected = selectedItems[selectedItems.length - 1];
    this.selectCourse(lastSelected);

    // Or if you want to process all selected items
    selectedItems.forEach(course => {
      this.selectCourse(course);
    });
  }

  //events in inputs
  selectCourse(courseSelected: any) {
    if (this.validateRepeatedProduct(courseSelected.id)) {
      this.clearOrderProduct();
      return;
    }
    if (this.product.quantity || this.product.quantity == 0) {
      this.product.quantity = 1;
    }
    this.product.productId = courseSelected.id;
    this.product.productName = courseSelected.name;
    this.product.price = typeof courseSelected.price == "string" ? parseInt(courseSelected.price) : courseSelected.price;
    this.product.totalProduct = this.product.price ? parseInt(courseSelected.price) * this.product.quantity : 0;

  }

  editPrice({ value }: any, product: OrderProduct) {
    product.price = typeof value == "string" ? parseInt(value) : value;
    this.editQuantity({ value: product.quantity }, product)
  }
  editQuantity({ value }: any, product: OrderProduct) {
    product.quantity = typeof value == "string" ? parseInt(value) : value;
    product.totalProduct = product.price * value;
  }

  calculeTotal() {
    this.order.total = 0;
    for (const prod of this.order.products) {
      if (prod.totalProduct) {
        this.order.total += prod.totalProduct;
      }
    }
  }
  validateRepeatedProduct(productId: string) {
    const foundValue = this.order.products.find(element => element.productId === productId);
    const invalid = foundValue !== undefined;
    if (invalid) {
      this.validate = true;
      this.courseError = "The course is already registered";

      showNotification(this.snackBar, "You already have the product selected", 'error');
    }
    return invalid;
  }
  validateAddProduct() {
    this.validate = false;
    if (!this.product.productId) {
      this.courseError = "Course id is required";
      this.validate = true;
    }
    if (!this.product.quantity) {
      this.quantityError = "Quantity is required";
      this.validate = true;
    } else if (this.product.quantity < 1) {
      this.quantityError = "Quantity must be greater than 0";
      this.validate = true;
    }
    if (!this.product.price) {
      this.priceError = "Price is required";
      this.validate = true;
    } else if (this.product.price < 1) {
      this.priceError = "Price must be greater than 0";
      this.validate = true;
    }
    if (this.validate) {
      return this.validate
    } else {
      this.courseError = null;
      this.quantityError = null;
      this.priceError = null;
      return this.validate;
    }
  }

  // Update loadAvailableCourses()
  loadAvailableCourses() {
    this._packageService.getAllProducts().subscribe({
      next: (response: any) => {
        this.availableCourses = response.data.map((product: any) => ({
          id: product._id,
          name: product.name,
          price: product.price // Make sure this matches your API response
        }));
        console.log("availableCourses", this.availableCourses);
      },


      error: (error) => {
        console.error('Failed to load courses:', error);
        showNotification(this.snackBar, 'Failed to load courses', 'error');
      }
    });
  }


  close() {
    console.log("call close ");
    this.modal.dismissAll();
  }

}
